/**
 * useNotificationThreadClosure — the bell's binding to the shared closure store
 * (2026-09-21, owner directive: "include the bell now").
 *
 * The store (services/groupThreads/notificationClosureStore) is pure and takes
 * its readers injected; this is the thin adapter that supplies the real ones
 * and wires the store's subscription into React. Everything interesting —
 * the ladder, the one-way latch, the retry bound, the owner reset — is over
 * there and testable without a browser.
 *
 * TWO PROPERTIES THE CALLERS DEPEND ON:
 *
 *  · IT COSTS NOTHING UNTIL ASKED. `enabled` is the panel being OPEN (the bell
 *    and the mobile sheet are closed by default and mount on every marketing
 *    page), so a signed-in person browsing /pricing issues no reads and holds
 *    no listener. /notifications passes `true` and the ten ids its current page
 *    renders, never the whole feed.
 *  · OFF IS BYTE-FOR-BYTE. With the lever off, or nobody signed in, the hook
 *    returns the store's frozen empty map — a stable identity, so no consumer
 *    re-memoizes — and never touches the store at all.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import {
  clearNotificationClosureNegatives,
  getNotificationClosures,
  resolveNotificationClosures,
  setNotificationClosureOwner,
  subscribeNotificationClosures,
  type NotificationClosureReaders,
} from '../services/groupThreads/notificationClosureStore';
import type { ThreadClosureFacts } from '../services/groupThreads/threadClosure';
import { isThreadTombstonedInStorage } from '../services/groupThreads/archivedTombstones';
import {
  MAIN_SURFACE_INSTANCE_ID,
  getChatThreadArchiveInfo,
} from '../repositories/chatThreadIndexRepository';
import { threadClosureAwarenessEnabled } from '../services/threadMentions/activityFeatureFlags';

const EMPTY: ReadonlyMap<string, ThreadClosureFacts> = Object.freeze(
  new Map<string, ThreadClosureFacts>(),
) as ReadonlyMap<string, ThreadClosureFacts>;

const NEVER_SUBSCRIBE = (): (() => void) => () => undefined;

export interface NotificationThreadClosureOptions {
  /** Test seam: the real readers are the repository + the tombstone leaf. */
  readers?: NotificationClosureReaders;
  /** Test seam: the lever, read from process.env by default. */
  enabledOverride?: boolean;
}

export function useNotificationThreadClosure(
  uid: string | null,
  threadIds: readonly string[],
  active: boolean,
  opts: NotificationThreadClosureOptions = {},
): ReadonlyMap<string, ThreadClosureFacts> {
  const leverOn = opts.enabledOverride ?? threadClosureAwarenessEnabled();
  const owner = (uid || '').trim();
  const on = Boolean(leverOn && owner && active);

  // The ids ride a VALUE key, not an array identity: the feed re-renders on
  // every snapshot and a fresh array each time would re-run the resolve
  // forever. The same planKey trick the chat's ladder uses.
  const idsKey = on ? Array.from(new Set(threadIds.filter(Boolean))).join(',') : '';

  const readers = useMemo<NotificationClosureReaders>(() => opts.readers ?? {
    isLocallyArchived: (threadId: string) =>
      isThreadTombstonedInStorage(MAIN_SURFACE_INSTANCE_ID, owner || null, threadId),
    readArchiveInfo: async (threadId: string) => {
      const info = await getChatThreadArchiveInfo(owner, threadId);
      return {
        archived: info.archived,
        atMs: info.atMs,
        ...(info.archivedBy ? { archivedBy: info.archivedBy } : {}),
        ...(info.archivedByName ? { archivedByName: info.archivedByName } : {}),
      };
    },
  }, [opts.readers, owner]);

  useEffect(() => {
    // Signing out clears the store; so does signing in as somebody else. One
    // person's answers about their own threads must never outlive them here.
    setNotificationClosureOwner(leverOn ? owner || null : null);
  }, [leverOn, owner]);

  // EVERY OPEN RE-ASKS ABOUT THE THREADS IT SAID WERE FINE.
  //
  // A negative is only true at the instant it was read: a thread answered
  // "open" when the panel was last opened and archived five minutes later
  // would otherwise stay open for the life of the tab, on all three surfaces
  // at once — a shared cache making the dead-end click MORE durable, not less.
  // Positives are kept, because archiving is one-way and a positive can only
  // have come from a tombstone or a document that said so. The cost is one
  // re-probe per open, cached for as long as the panel stays open.
  useEffect(() => {
    if (on) clearNotificationClosureNegatives();
  }, [on]);

  useEffect(() => {
    if (!on || !idsKey) return;
    void resolveNotificationClosures(idsKey.split(','), readers);
  }, [on, idsKey, readers]);

  // Off means OFF: a no-op subscribe (a stable module constant, so React does
  // not churn the subscription) leaves the store with literally no listener,
  // rather than a cheap-but-present one.
  const live = useSyncExternalStore(
    on ? subscribeNotificationClosures : NEVER_SUBSCRIBE,
    getNotificationClosures,
    getNotificationClosures,
  );
  return on ? live : EMPTY;
}

export default useNotificationThreadClosure;
